import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, BarChart3, Package, TrendingUp, TrendingDown, AlertCircle, 
  ShoppingCart, Percent, Target, RefreshCw, Calendar, ChevronDown, ChevronUp,
  Layers
} from 'lucide-react';
import HubLayout from '../../components/HubLayout';
import { api } from '../../api/client';

// Import existing components
import VeeqoOrders from '../Sales/VeeqoOrders';
import AmazonSettlements from '../Finance/AmazonSettlements';
import ForecastTab from './ForecastTab';
import GoToMarketStrategy from './GoToMarketStrategy';

// ============================================================
// METRIC CARD COMPONENT - EXACTLY matches Dashboard Hub style
// ============================================================
const MetricCard = ({ icon, label, value, subtitle, status, onClick }) => {
  // Status dot colors
  const statusColors = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    blue: 'bg-blue-500',
    default: 'bg-gray-500'
  };

  return (
    <div 
      onClick={onClick}
      className={`bg-[#1a2744] rounded-lg p-3 relative min-h-[80px] min-w-[130px] max-w-[160px] flex-shrink-0 ${onClick ? 'cursor-pointer hover:bg-[#1e2f52]' : ''} transition-colors`}
    >
      {/* Status dot - top right, small */}
      {status && (
        <div className={`absolute top-2 right-2 w-1.5 h-1.5 rounded-full ${statusColors[status] || statusColors.default}`} />
      )}
      
      {/* Icon + Label - very small */}
      <div className="flex items-center gap-1 text-gray-400 text-[10px] uppercase tracking-wide mb-0.5">
        {icon}
        <span>{label}</span>
      </div>
      
      {/* Value - medium-large, not huge */}
      <div className="text-white text-xl font-bold leading-tight">
        {value}
      </div>
      
      {/* Subtitle - tiny */}
      {subtitle && (
        <div className="text-gray-500 text-[10px]">
          {subtitle}
        </div>
      )}
    </div>
  );
};

// ============================================================
// COST TILE COMPONENT - Compact, fixed width
// ============================================================
const CostTile = ({ icon, label, value, percentage, color }) => {
  const formatCurrency = (val) => {
    const numVal = Number(val) || 0;
    if (numVal >= 1000) return `$${(numVal / 1000).toFixed(1)}K`;
    return `$${numVal.toFixed(0)}`;
  };

  const hasData = value > 0;

  return (
    <div className="bg-[#1a2744] rounded-lg p-3 min-w-[120px] max-w-[150px] flex-shrink-0">
      <div className="flex items-center gap-1 mb-1">
        {icon}
        <span className="text-[10px] text-gray-400 font-medium uppercase">{label}</span>
      </div>
      {hasData ? (
        <>
          <div className="text-lg font-bold text-white">{formatCurrency(value)}</div>
          <div className="text-[10px] text-red-400">{percentage}%</div>
        </>
      ) : (
        <>
          <div className="text-lg font-bold text-gray-500">$0</div>
          <div className="text-[10px] text-gray-500 italic">No data</div>
        </>
      )}
    </div>
  );
};

// ============================================================
// PROFIT WATERFALL CHART - Horizontal bar chart style
// Shows "No data" indicator when costs are $0
// ============================================================
const ProfitWaterfallChart = ({ revenue, costs, profit }) => {
  const maxValue = Math.max(revenue, 1);
  
  // Check if we have any cost data
  const hasCostData = (costs.cogs || 0) > 0 || (costs.ads || 0) > 0 || 
                      (costs.channel_fees || 0) > 0 || (costs.fba_fees || 0) > 0 || 
                      (costs.tax || 0) > 0;
  
  const items = [
    { label: 'Revenue', value: revenue, color: '#10b981', isPositive: true },
    { label: 'COGS', value: costs.cogs || 0, color: '#ef4444', isPositive: false },
    { label: 'Ads', value: costs.ads || 0, color: '#f59e0b', isPositive: false },
    { label: 'Channel', value: costs.channel_fees || 0, color: '#8b5cf6', isPositive: false },
    { label: 'FBA', value: costs.fba_fees || 0, color: '#3b82f6', isPositive: false },
    { label: 'Tax', value: costs.tax || 0, color: '#6b7280', isPositive: false },
  ];

  const formatCurrency = (val) => {
    const absVal = Math.abs(Number(val) || 0);
    if (absVal >= 1000) return `$${(absVal / 1000).toFixed(1)}K`;
    return `$${absVal.toFixed(0)}`;
  };

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-3">
          <span className="text-xs text-gray-400 w-16">{item.label}</span>
          <div className="flex-1 bg-slate-900 rounded h-5 overflow-hidden">
            <div 
              className="h-full rounded transition-all duration-500"
              style={{ 
                width: item.value > 0 ? `${Math.min((Math.abs(item.value) / maxValue) * 100, 100)}%` : '0%',
                backgroundColor: item.color,
                opacity: 0.8
              }}
            />
          </div>
          {item.value > 0 ? (
            <span className={`text-xs font-medium w-20 text-right ${item.isPositive ? 'text-green-400' : 'text-red-400'}`}>
              {item.isPositive ? '' : '-'}{formatCurrency(item.value)}
            </span>
          ) : (
            <span className="text-xs text-gray-500 w-20 text-right italic">
              {item.label === 'Revenue' ? '$0' : 'No data'}
            </span>
          )}
        </div>
      ))}
      
      {/* Separator line */}
      <div className="border-t border-slate-700 my-2"></div>
      
      {/* Profit row */}
      <div className="flex items-center gap-3">
        <span className="text-xs text-white font-semibold w-16">= Profit</span>
        <div className="flex-1 bg-slate-900 rounded h-5 overflow-hidden">
          <div 
            className="h-full rounded transition-all duration-500"
            style={{ 
              width: `${Math.min((Math.abs(profit) / maxValue) * 100, 100)}%`,
              backgroundColor: profit >= 0 ? '#10b981' : '#ef4444',
              opacity: 1
            }}
          />
        </div>
        <span className={`text-xs font-bold w-20 text-right ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {formatCurrency(profit)}
        </span>
      </div>
      
      {/* No cost data warning */}
      {!hasCostData && revenue > 0 && (
        <div className="mt-3 p-2 bg-amber-900/20 border border-amber-600/30 rounded text-xs text-amber-400 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          Cost data not mapped from Veeqo. Profit = Revenue when costs are $0.
        </div>
      )}
    </div>
  );
};

// ============================================================
// CHANNEL TABLE - Sales by channel with modern icons
// ============================================================
const ChannelTable = ({ channels }) => {
  // Use emoji as fallback, but could be replaced with actual icons
  const channelConfig = {
    'Amazon store FBA': { color: '#ff9900', icon: '🟠' },
    'Amazon store': { color: '#ff9900', icon: '🟠' },
    'Amazon FBA': { color: '#ff9900', icon: '🟠' },
    'Amazon FBM': { color: '#e68a00', icon: '🟡' },
    'Walmart': { color: '#0071ce', icon: '🔵' },
    'eBay': { color: '#e53238', icon: '🔴' },
    'Shopify': { color: '#96bf48', icon: '🟢' },
    'Veeqo': { color: '#00b8d4', icon: '🔗' },
    'Faire': { color: '#f97316', icon: '🎁' },
    'PetYupp': { color: '#8b5cf6', icon: '🐕' },
    'TikTok': { color: '#010101', icon: '⚫' },
    'Manual': { color: '#6b7280', icon: '📝' },
    'Merged Orders': { color: '#6b7280', icon: '🔗' },
    'Direct': { color: '#64748b', icon: '🏪' },
    'default': { color: '#6b7280', icon: '⚪' }
  };

  const getChannelConfig = (channelName) => {
    // Find matching channel config
    for (const key of Object.keys(channelConfig)) {
      if (channelName?.toLowerCase().includes(key.toLowerCase())) {
        return channelConfig[key];
      }
    }
    return channelConfig.default;
  };

  const formatCurrency = (val) => {
    const numVal = Number(val) || 0;
    if (numVal >= 1000) return `$${(numVal / 1000).toFixed(1)}K`;
    return `$${numVal.toFixed(0)}`;
  };

  // Calculate totals
  const totals = channels.reduce((acc, ch) => ({
    orders: acc.orders + (ch.orders || 0),
    revenue: acc.revenue + (ch.revenue || 0),
    profit: acc.profit + (ch.profit || 0)
  }), { orders: 0, revenue: 0, profit: 0 });

  const avgMargin = totals.revenue > 0 ? (((totals.profit / totals.revenue) * 100) || 0).toFixed(1) : '0';

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-700">
            <th className="text-left py-2 px-3 text-xs text-gray-400 font-medium">Channel</th>
            <th className="text-right py-2 px-3 text-xs text-gray-400 font-medium">Orders</th>
            <th className="text-right py-2 px-3 text-xs text-gray-400 font-medium">Revenue</th>
            <th className="text-right py-2 px-3 text-xs text-gray-400 font-medium">Profit</th>
            <th className="text-right py-2 px-3 text-xs text-gray-400 font-medium">Margin</th>
          </tr>
        </thead>
        <tbody>
          {channels.map((channel, index) => {
            const config = getChannelConfig(channel.channel);
            return (
              <tr key={index} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                <td className="py-2 px-3 text-white">
                  <span className="mr-2">{config.icon}</span>
                  {channel.channel || 'Unknown'}
                </td>
                <td className="py-2 px-3 text-right text-gray-300">{channel.orders?.toLocaleString()}</td>
                <td className="py-2 px-3 text-right text-white font-medium">{formatCurrency(channel.revenue)}</td>
                <td className={`py-2 px-3 text-right font-medium ${channel.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency(channel.profit)}
                </td>
                <td className={`py-2 px-3 text-right ${
                  channel.margin >= 50 ? 'text-green-400' : 
                  channel.margin >= 30 ? 'text-yellow-400' : 'text-orange-400'
                }`}>
                  {(Number(channel.margin) || 0).toFixed(1)}%
                </td>
              </tr>
            );
          })}
        </tbody>
        {/* Totals row */}
        <tfoot>
          <tr className="border-t-2 border-slate-600 bg-slate-700/30 font-semibold">
            <td className="py-2 px-3 text-white">Total</td>
            <td className="py-2 px-3 text-right text-white">{totals.orders.toLocaleString()}</td>
            <td className="py-2 px-3 text-right text-white">{formatCurrency(totals.revenue)}</td>
            <td className={`py-2 px-3 text-right ${totals.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(totals.profit)}
            </td>
            <td className="py-2 px-3 text-right text-yellow-400">{avgMargin}%</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ============================================================
// DATE RANGE SELECTOR - Updated with user's preferred presets
// ============================================================
const DateRangeSelector = ({ value, onChange }) => {
  const options = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },  // Default
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'month_to_date', label: 'Month to Date' },
    { value: 'year_to_date', label: 'Year to Date' },
  ];

  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-slate-800 border border-slate-700 text-white text-xs rounded-lg px-3 py-2 pr-8 appearance-none cursor-pointer hover:bg-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  );
};

// ============================================================
// MAIN SALES HUB COMPONENT
// ============================================================
const SalesHub = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('analytics');
  const [dateRange, setDateRange] = useState('last_30_days');
  const [loading, setLoading] = useState(true);
  const [waterfallExpanded, setWaterfallExpanded] = useState(false); // Collapsed by default
  const [channelExpanded, setChannelExpanded] = useState(false); // Collapsed by default
  
  // Analytics data
  const [analytics, setAnalytics] = useState(null);
  const [channels, setChannels] = useState([]);
  const [losses, setLosses] = useState(0);

  // Calculate date range - Updated with Month to Date and Year to Date
  const getDateRange = (range) => {
    const end = new Date();
    const start = new Date();
    
    switch(range) {
      case 'today':
        start.setHours(0,0,0,0);
        break;
      case 'yesterday':
        start.setDate(start.getDate() - 1);
        start.setHours(0,0,0,0);
        end.setDate(end.getDate() - 1);
        end.setHours(23,59,59,999);
        break;
      case 'last_7_days':
        start.setDate(start.getDate() - 7);
        break;
      case 'last_30_days':
        start.setDate(start.getDate() - 30);
        break;
      case 'this_month':
        start.setDate(1);
        break;
      case 'last_month':
        start.setMonth(start.getMonth() - 1);
        start.setDate(1);
        end.setDate(0);
        break;
      case 'month_to_date':
        start.setDate(1);
        start.setHours(0,0,0,0);
        break;
      case 'year_to_date':
        start.setMonth(0);
        start.setDate(1);
        start.setHours(0,0,0,0);
        break;
      default:
        start.setDate(start.getDate() - 30);
    }
    
    return {
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0]
    };
  };

  // Fetch all analytics data
  const fetchAnalytics = async () => {
    setLoading(true);
    const { start_date, end_date } = getDateRange(dateRange);
    
    try {
      const [analyticsRes, channelsRes, lossesRes] = await Promise.all([
        api.get(`/api/v1/veeqo/analytics?start_date=${start_date}&end_date=${end_date}`, {
          headers: { 'X-Tenant-ID': 'tenant-123' }
        }),
        api.get(`/api/v1/veeqo/channels?start_date=${start_date}&end_date=${end_date}`, {
          headers: { 'X-Tenant-ID': 'tenant-123' }
        }),
        api.get(`/api/v1/veeqo/losses?start_date=${start_date}&end_date=${end_date}`, {
          headers: { 'X-Tenant-ID': 'tenant-123' }
        })
      ]);
      
      setAnalytics(analyticsRes);
      setChannels(channelsRes.channels || []);
      setLosses(lossesRes.losses || 0);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  // Format helpers
  const formatCurrency = (val) => {
    const numVal = Number(val) || 0;
    if (!numVal) return '$0';
    if (numVal >= 1000000) return `$${(numVal / 1000000).toFixed(1)}M`;
    if (numVal >= 1000) return `$${(numVal / 1000).toFixed(1)}K`;
    return `$${numVal.toFixed(0)}`;
  };

  const formatNumber = (val) => {
    if (!val) return '0';
    return new Intl.NumberFormat('en-US').format(val);
  };

  const tabs = [
    { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="w-3.5 h-3.5" /> },
    { id: 'orders', label: 'Orders', icon: <ShoppingCart className="w-3.5 h-3.5" /> },
    { id: 'settlements', label: 'Settlements', icon: <Layers className="w-3.5 h-3.5" /> },
    { id: 'forecast', label: 'Forecast', icon: <TrendingUp className="w-3.5 h-3.5" /> },
    { id: 'gtm', label: 'Go-to-Market', icon: <Target className="w-3.5 h-3.5" /> }
  ];

  const actions = (
    <div className="flex items-center gap-2">
      <DateRangeSelector value={dateRange} onChange={setDateRange} />
      <button 
        onClick={fetchAnalytics}
        disabled={loading}
        className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        Refresh
      </button>
    </div>
  );

  return (
    <HubLayout
      title="Sales Hub"
      subtitle="Revenue, profit, orders, and sales analytics"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      actions={actions}
    >
      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-4">
          {/* Row 1: Key Metrics Cards - Compact flex layout like Dashboard Hub */}
          <div className="flex flex-wrap gap-2">
            <MetricCard 
              icon={<DollarSign className="w-4 h-4" />}
              label="REVENUE"
              value={formatCurrency(analytics?.summary?.revenue)}
              subtitle={`${formatNumber(analytics?.summary?.orders || 0)} orders`}
              status="green"
            />
            <MetricCard 
              icon={<ShoppingCart className="w-4 h-4" />}
              label="ORDERS"
              value={formatNumber(analytics?.summary?.orders)}
              subtitle={dateRange.replace(/_/g, ' ')}
              status="blue"
            />
            <MetricCard 
              icon={<Package className="w-4 h-4" />}
              label="UNITS"
              value={formatNumber(analytics?.summary?.units || analytics?.summary?.orders)}
              subtitle="items sold"
              status="default"
            />
            <MetricCard 
              icon={<TrendingUp className="w-4 h-4" />}
              label="PROFIT"
              value={formatCurrency(analytics?.summary?.profit)}
              subtitle={`${analytics?.summary?.margin || 0}% margin`}
              status={analytics?.summary?.profit > 0 ? 'green' : 'red'}
            />
            <MetricCard 
              icon={<Percent className="w-4 h-4" />}
              label="MARGIN"
              value={`${analytics?.summary?.margin || 0}%`}
              subtitle="gross margin"
              status={analytics?.summary?.margin > 30 ? 'green' : analytics?.summary?.margin > 15 ? 'yellow' : 'red'}
            />
            <MetricCard 
              icon={<Target className="w-4 h-4" />}
              label="ACOS"
              value={`${analytics?.summary?.acos || 0}%`}
              subtitle="ad cost of sale"
              status={analytics?.summary?.acos < 20 ? 'green' : analytics?.summary?.acos < 35 ? 'yellow' : 'red'}
            />
          </div>

          {/* Row 2: Profit Waterfall - Collapsible (collapsed by default) */}
          <div className="bg-[#1a2744] rounded-lg p-3">
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setWaterfallExpanded(!waterfallExpanded)}
            >
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-green-400" />
                Profit Waterfall
              </h3>
              <button className="text-gray-400 hover:text-white transition-colors p-1">
                {waterfallExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
            
            {/* Collapsed summary - always visible when collapsed */}
            {!waterfallExpanded && (
              <div className="text-gray-400 text-[10px] mt-1">
                Revenue {formatCurrency(analytics?.summary?.revenue)} → Profit {formatCurrency(analytics?.summary?.profit)} ({analytics?.summary?.margin || 0}% margin)
              </div>
            )}
            
            {/* Expanded content */}
            {waterfallExpanded && (
              <div className="mt-3 pt-3 border-t border-slate-700">
                <ProfitWaterfallChart 
                  revenue={analytics?.summary?.revenue || 0}
                  costs={analytics?.costs || {}}
                  profit={analytics?.summary?.profit || 0}
                />
              </div>
            )}
          </div>

          {/* Row 3: Cost Breakdown - Compact flex layout */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-400" />
              Cost Breakdown
            </h3>
            <div className="flex flex-wrap gap-2">
              <CostTile 
                icon={<Package className="w-4 h-4 text-red-400" />}
                label="COGS"
                value={analytics?.costs?.cogs || 0}
                percentage={((analytics?.costs?.cogs || 0) / Math.max(analytics?.summary?.revenue || 1, 1) * 100).toFixed(1)}
                color="#ef4444"
              />
              <CostTile 
                icon={<Target className="w-4 h-4 text-yellow-400" />}
                label="ADS"
                value={analytics?.costs?.ads || 0}
                percentage={((analytics?.costs?.ads || 0) / Math.max(analytics?.summary?.revenue || 1, 1) * 100).toFixed(1)}
                color="#f59e0b"
              />
              <CostTile 
                icon={<Layers className="w-4 h-4 text-purple-400" />}
                label="CHANNEL"
                value={analytics?.costs?.channel_fees || 0}
                percentage={((analytics?.costs?.channel_fees || 0) / Math.max(analytics?.summary?.revenue || 1, 1) * 100).toFixed(1)}
                color="#8b5cf6"
              />
              <CostTile 
                icon={<Package className="w-4 h-4 text-blue-400" />}
                label="FBA FEES"
                value={analytics?.costs?.fba_fees || 0}
                percentage={((analytics?.costs?.fba_fees || 0) / Math.max(analytics?.summary?.revenue || 1, 1) * 100).toFixed(1)}
                color="#3b82f6"
              />
              <CostTile 
                icon={<DollarSign className="w-4 h-4 text-gray-400" />}
                label="TAX"
                value={analytics?.costs?.tax || 0}
                percentage={((analytics?.costs?.tax || 0) / Math.max(analytics?.summary?.revenue || 1, 1) * 100).toFixed(1)}
                color="#6b7280"
              />
            </div>
          </div>

          {/* Row 4: Sales Split - Compact flex layout */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-cyan-400" />
              Sales Split: Organic vs Sponsored
            </h3>
            <div className="flex gap-2">
              <div className="bg-[#1a2744] rounded-lg p-3 min-w-[160px] max-w-[220px]">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-[10px] text-gray-400 font-medium uppercase">ORGANIC</span>
                </div>
                <div className="text-xl font-bold text-white">{formatCurrency(analytics?.sales_split?.organic_revenue)}</div>
                <div className="text-[10px] text-gray-500">{formatNumber(analytics?.sales_split?.organic_units)} units</div>
              </div>
              <div className="bg-[#1a2744] rounded-lg p-3 min-w-[160px] max-w-[220px]">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                  <span className="text-[10px] text-gray-400 font-medium uppercase">SPONSORED</span>
                </div>
                <div className="text-xl font-bold text-white">{formatCurrency(analytics?.sales_split?.sponsored_revenue)}</div>
                <div className="text-[10px] text-gray-500">{formatNumber(analytics?.sales_split?.sponsored_units)} units</div>
              </div>
            </div>
          </div>

          {/* Row 5: Channel Performance - Collapsible (collapsed by default) */}
          <div className="bg-[#1a2744] rounded-lg p-3">
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setChannelExpanded(!channelExpanded)}
            >
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Layers className="w-4 h-4 text-purple-400" />
                Channel Performance
              </h3>
              <button className="text-gray-400 hover:text-white transition-colors p-1">
                {channelExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
            
            {/* Collapsed summary - always visible when collapsed */}
            {!channelExpanded && channels.length > 0 && (
              <div className="text-gray-400 text-[10px] mt-1">
                {channels.length} channels • Top: {channels[0]?.channel} ({channels[0]?.orders} orders, {formatCurrency(channels[0]?.revenue)})
              </div>
            )}
            
            {/* Expanded table - only when open */}
            {channelExpanded && (
              <div className="mt-3 pt-3 border-t border-slate-700">
                {channels.length > 0 ? (
                  <ChannelTable channels={channels} />
                ) : (
                  <div className="text-center py-4 text-gray-400 text-sm">
                    No channel data available
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Losses Alert */}
          {losses > 0 && (
            <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-400">
                <AlertCircle className="w-5 h-5" />
                <span className="font-semibold">{losses} orders with negative profit margin need attention</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && <VeeqoOrders dateRange={dateRange} />}

      {/* Settlements Tab */}
      {activeTab === 'settlements' && <AmazonSettlements />}

      {/* Forecast Tab */}
      {activeTab === 'forecast' && <ForecastTab />}

      {/* Go-to-Market Strategy Tab */}
      {activeTab === 'gtm' && <GoToMarketStrategy />}
    </HubLayout>
  );
};

export default SalesHub;
